import { CdkPortalOutlet, Portal } from '@angular/cdk/portal';

import { AsyncPipe } from '@angular/common';
import { Component, DOCUMENT, HostBinding, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { tracking } from '@ecs-helpers';
import { OverlayService } from '@ecs-services/overlay/overlay.service';
import { OnDestroyBase, TrackingService, WINDOW } from '@os-components';
import { SewButton } from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'ecs-overlay-right',
    templateUrl: './overlay-right.component.html',
    styleUrls: ['./overlay-right.component.scss'],
    imports: [SewButton, CdkPortalOutlet, AsyncPipe],
})
export class OverlayRightComponent extends OnDestroyBase implements OnDestroy, OnInit {
    @HostBinding('class.closed') public closed = true;

    public overlayPortal!: Observable<Portal<any> | null>;

    public constructor(
        private overlayService: OverlayService,
        private trackingService: TrackingService,
        @Inject(WINDOW) private window: Window,
        @Inject(DOCUMENT) private document: Document
    ) {
        super();
    }

    /**
     * Listens to the `scroll` and `resize` event and sets the height of the overlay-content,
     * that it uses the full height while being sticky.
     */
    @HostListener('window:scroll')
    @HostListener('window:resize')
    public onWindowScrollAndResize() {
        let header;
        let spaceToRemove = 0;

        /**
         * If the website get's scrolled down, the host element of the header won't display the correct `top` value.
         * The property is used in the method `inViewport`. This would result into the sidebar being too short.
         * For this reason, we are using the headers shadowRoot element `header` which has the correct `top` in this case.
         *
         * In case the header is not scrolled (and the scrollbar is at the top of the page) the `header` element does not reflect
         * the correct height and therefore also not `top`. That's why we use the host element, which has the correct value in this case.
         *
         * Related bug: #20230427-0770
         */
        if (this.window.scrollY === 0) {
            header = this.document.querySelector<HTMLElement>('sew-ws-header');
        } else {
            header = this.document
                .querySelector<HTMLElement>('sew-ws-header')
                ?.shadowRoot?.querySelector('header');
        }

        if (header) {
            spaceToRemove = this.inViewport(header);
        }

        const footer = this.document
            .querySelector<HTMLElement>('sew-ws-footer-api')
            ?.shadowRoot?.querySelector('sew-ws-footer');
        if (footer) {
            spaceToRemove = spaceToRemove + this.inViewport(footer);
        }

        let overlayContent = this.document.querySelector<HTMLElement>('ecs-overlay-right');

        if (overlayContent) {
            overlayContent = overlayContent.querySelector<HTMLElement>('.content');
        }

        if (overlayContent) {
            overlayContent.style.height = `calc(100vh - ${spaceToRemove}px)`;
        }
    }

    public ngOnInit() {
        this.overlayService.overlayStateOpen.pipe(takeUntil(this.destroySubject)).subscribe((state) => {
            this.closed = !state;
            if (state) {
                this.onWindowScrollAndResize();
            }
        });
        // use delay to prevent NG0100: ExpressionChangedAfterItHasBeenChecked Error: Expression has changed after it was checked.
        // based on https://blog.angular-university.io/angular-debugging/
        this.overlayPortal = this.overlayService.overlayPortal.pipe(delay(0));
    }

    public onClose() {
        this.overlayService.close();
        this.trackingService.trackEvent(tracking.step.overlayRight.close);
    }

    // based on https://stackoverflow.com/questions/24768795/get-the-visible-height-of-a-div-with-jquery
    // with the code example http://jsfiddle.net/tw6g2oeu/734/
    private inViewport(element: HTMLElement) {
        const elementHeight = element.offsetHeight;
        const windowHeight = this.window.innerHeight;
        const elementPositioning = element.getBoundingClientRect();
        const viewPortTopToElementTop = elementPositioning.top;
        const viewPortTopToElementBottom = elementPositioning.bottom;

        return Math.max(
            0,
            viewPortTopToElementTop > 0
                ? Math.min(elementHeight, windowHeight - viewPortTopToElementTop)
                : Math.min(viewPortTopToElementBottom, windowHeight)
        );
    }
}
